import {
	formatDecimalNumber,
	formatDuration,
	formatTime,
	getISODate,
	getISOTime,
	parseDateInput,
	parseDecimalNumber,
	parseDuration,
	parseTimeInput
} from "@/shared/utils/datefunctions";

export interface FieldValueChangeEventInfo<T, K extends keyof T> {
	selector: string;
	field: K;
	oldValue: T[K];
	newValue: T[K];
}

export type FieldValueExtractor<T, K extends keyof T> = (selector: string) => T[K];

export class FieldObserver<T> {
	private static FIELD_VALUE_CHANGE_EVENT = "fieldValueChange";

	private model: T;
	private changed: boolean;

	constructor() {
		this.changed = false;
	}

	public setModel(model: T) {
		this.model = model;
		this.changed = false;
	}

	public observeInput<K extends keyof T>(selector: string, modelField: K, extractor?: FieldValueExtractor<T, K>, preventSetChanged?: boolean, forceChangeEvent?: boolean) {
		if (extractor == null) {	// == because it checks for null and undefined
			extractor = this.findSuitableExtractor(selector);
		}

		$(selector).change(() => {
			const oldValue = this.model[modelField];
			const newValue = extractor(selector);

			if (forceChangeEvent || oldValue !== newValue) {
				this.changed = !preventSetChanged;
				this.model[modelField] = newValue;

				$(this).triggerHandler(FieldObserver.FIELD_VALUE_CHANGE_EVENT, {
					selector: selector,
					field: modelField,
					oldValue: oldValue,
					newValue: newValue
				} as FieldValueChangeEventInfo<T, K>);
			}
		});
	}

	public getValue<K extends keyof T>(modelField: K) {
		return this.model[modelField];
	}

	private findSuitableExtractor<K extends keyof T>(selector: string): FieldValueExtractor<T, K> {
		if ($(selector).is("input[type='checkbox']")) {
			return (sel) => $(sel).is(":checked") as unknown as T[K];
		}
		if ($(selector).is("input[type='radio']")) {
			return (sel) => $(sel).filter(":checked").val() as unknown as T[K];
		}

		return (sel) => $(sel).val() as unknown as T[K];
	}

	public hasChanged(): boolean {
		return this.changed;
	}

	public onFieldValueChange(handler: (event: JQuery.TriggeredEvent, data: FieldValueChangeEventInfo<T, keyof T>) => void) {
		$(this).on(FieldObserver.FIELD_VALUE_CHANGE_EVENT, handler);
	}

	/**
	 * Returns the duration in milliseconds from the field or 0 if the field is empty.
	 * @param selector
	 */
	public static readonly getDurationFromField: (selector: string) => number = (selector: string) => {
		return parseDuration($(selector).val() as string, true) * 60000;
	}

	/**
	 * Returns the duration in milliseconds from the field or null if the field is empty.
	 * @param selector
	 */
	public static readonly getNullableDurationFromField: (selector: string) => null | number = (selector: string) => {
		const durationString = $(selector).val() as string;
		if (durationString == null || durationString === "") {
			return null;
		} else {
			return parseDuration(durationString, true) * 60000;
		}
	}

	public static getDecimalNumberFromField = (selector: string) => {
		return parseDecimalNumber($(selector).val() as string);
	}

	public static getNullableNumberFromField = (selector: string) => {
		const n = Number.parseInt($(selector).val() as string, 10);
		return Number.isNaN(n) ? null : n;
	}

	public static getDateJsonFromField = (selector: string) => {
		return getISODate(parseDateInput($(selector).val() as string));
	}

	public static setTimeField = (selector: string, time: string) => {
		$(selector).val(formatTime(parseTimeInput((time))));
	}

	public static getTimeField = (selector: string): string => {
		return getISOTime(parseTimeInput($(selector).val() as string));
	}

	public static readonly setDurationFieldWithEmptyOnZero = (selector: string, duration: number) => {
		$(selector).val(duration ? formatDuration(duration) : "");
	}

	/**
	 * Sets the duration field to the given duration or empty if the duration is null.
	 */
	public static readonly setDurationField = (selector: string, duration: number) => {
		$(selector).val(duration != null ? formatDuration(duration) : "");
	}

	public static getCheckboxState = (selector: string): boolean => $(selector).prop("checked") as boolean;

	public static getEnumValueFromRadio<TEnum extends string>(enumType: Record<string, TEnum>) {
		const validValues = new Set(Object.values(enumType));
		return (selector: string): TEnum | undefined => {
			const value = $(selector).filter(":checked").val();
			return typeof value === "string" && validValues.has(value as TEnum) ? value as TEnum : undefined;
		}
	}

	public static readonly getPercentageFromField = (selector: string) => Number($(selector).val()) / 100;

	public static readonly setPercentageField = (selector: string, percent: number) => {
		$(selector).val(percent ? formatDecimalNumber(percent * 100, 0) : "");
	}
}
